import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from 'typesafe-actions';
import { ILimitOrder, ILimitOrdersResponse, enums } from '@kattana-io/common';
import { LOG_OUT, SET_AUTH } from '../actions/auth';
import {
  actionSetLimitOrders,
  ACTION_GET_LIMIT_ORDERS,
  ACTION_DELETE_LIMIT_ORDER,
  actionDeletedLimitOrder,
  ACTION_UPDATE_LIMIT_ORDER_PRICE,
  actionAfterUpdateLimitOrderPrice,
  IDeleteLimitOrderPayload,
  IUpdateLimitOrderPayload,
  IGetLimitOrdersPayload,
  actionGetLimitOrders,
  ACTION_CONSUME_LIMIT_ORDER,
  ACTION_CREATE_LIMIT_ORDER,
  ICreateLimitOrderPyload,
  actionSetLimitOrdersTickers,
  actionAddLimitOrder,
  actionSetCreatingLimitOrders,
} from '../actions/limit_orders';
import { actionCreateNotification } from '../actions/notifications';
import { IStore } from '../reducers';
import { authTokenSelector } from '../selectors/auth';
import { KattanaAPI, LimitOrderAPI } from '../store/createStore';

const NOT_CREATED_ORDER = "Couldn't create a limit order";
const CREATED_ORDER = 'You have successfully placed a limit order';

function* onGetLimitOrders(
  action: PayloadAction<any, IGetLimitOrdersPayload>
): any {
  const { wallet, token } = action.payload;

  try {
    const data: ILimitOrdersResponse = yield call(
      LimitOrderAPI.get,
      wallet,
      token
    );

    if (data) {
      yield put(actionSetLimitOrders(data));
    } else {
      console.warn('Could not load limit orders');
      yield put(actionSetLimitOrders({ limit_orders: [], tickers: {} }));
    }
  } catch (err) {
    yield put(actionSetLimitOrders({ limit_orders: [], tickers: {} }));
    console.error(err);
  }
}

function* onCreateLimitOrder(
  action: PayloadAction<any, ICreateLimitOrderPyload>
): any {
  try {
    yield put(actionSetCreatingLimitOrders(true));

    const { payload, executionType, callback } = action.payload;
    const token = yield select(authTokenSelector);

    let response: any;
    if (executionType === enums.ExecutionType.GRID) {
      response = yield call(LimitOrderAPI.createGrid, payload, token);
    } else if (executionType === enums.ExecutionType.LIMIT) {
      response = yield call(LimitOrderAPI.create, payload, token);
    } else {
      response = yield call(LimitOrderAPI.createWhale, payload, token);
    }

    if (!response || response instanceof Error) {
      yield put(
        actionCreateNotification({
          kind: 'warning',
          message: NOT_CREATED_ORDER,
        })
      );
    } else {
      yield put(
        actionCreateNotification({
          kind: 'info',
          message: CREATED_ORDER,
        })
      );

      const tickers = yield select(
        (state: IStore) => state.limit_orders.tickers
      );
      const key = `${payload.payload.chain}-${payload.payload.pair}`;
      if (!tickers[key]) {
        const group = yield call(KattanaAPI.tickers, {
          chain: payload.payload.chain,
          address: payload.payload.pair,
        });
        if (Array.isArray(group) && group[0]) {
          const item = group[0];
          yield put(actionSetLimitOrdersTickers({ ...tickers, [key]: item }));
        }
      }

      /**
       * Refresh limit orders and refetch balances for wallet
       */
      yield put(actionAddLimitOrder(response));
      yield call(callback);
    }
  } catch (error) {
    yield put(
      actionCreateNotification({ kind: 'warning', message: NOT_CREATED_ORDER })
    );
    console.error(error);
  } finally {
    yield put(actionSetCreatingLimitOrders(false));
  }
}

function* onDeleteLimitOrder(
  action: PayloadAction<any, IDeleteLimitOrderPayload>
): any {
  try {
    const data: any = yield call(
      LimitOrderAPI.remove,
      action.payload.data,
      action.payload.token
    );
    if (data) {
      yield put(
        actionCreateNotification({ kind: 'info', message: 'Order canceled' })
      );
      // Call reducer
      yield put(actionDeletedLimitOrder(action.payload));
    } else {
      yield put(
        actionCreateNotification({
          kind: 'warning',
          message: 'Something went wrong',
        })
      );
    }
  } catch (err) {
    console.error(err);
  }
}

function* onConsumeLimitOrder(action: PayloadAction<any, ILimitOrder>): any {
  try {
    const token = yield select(authTokenSelector);
    const body: any = {
      uuid: action.payload.uuid,
      wallet: action.payload.wallet,
      data: {
        status: enums.LimitOrderStatus.CONSUMED,
      },
    };
    yield call(LimitOrderAPI.update, body, token);
  } catch (error) {
    console.error(error);
  }
}

function* onUpdateLimitOrder(
  action: PayloadAction<any, IUpdateLimitOrderPayload>
): any {
  try {
    const data: any = yield call(
      LimitOrderAPI.update,
      action.payload.data,
      action.payload.token
    );
    if (data) {
      yield put(
        actionCreateNotification({ kind: 'info', message: 'Order updated' })
      );
      // Call reducer
      yield put(actionAfterUpdateLimitOrderPrice(action.payload));
    } else {
      yield put(
        actionCreateNotification({
          kind: 'warning',
          message: 'Something went wrong',
        })
      );
    }
  } catch (err) {
    console.error(err);
  }
}

function* onAuth(): any {
  try {
    const token = yield select(authTokenSelector);
    const { wallet } = yield select((store: IStore) => store.auth);

    yield put(
      actionGetLimitOrders({
        token,
        wallet,
      })
    );
  } catch (error) {
    console.error(error);
  }
}

function* onLogOut(): any {
  try {
    yield put(actionSetLimitOrders({ limit_orders: [], tickers: {} }));
  } catch (error) {
    console.error(error);
  }
}

export default function* limitOrdersSaga() {
  yield takeLatest(SET_AUTH, onAuth);
  yield takeLatest(LOG_OUT, onLogOut);
  yield takeLatest(ACTION_GET_LIMIT_ORDERS, onGetLimitOrders);
  yield takeEvery(ACTION_CREATE_LIMIT_ORDER, onCreateLimitOrder);
  yield takeEvery(ACTION_DELETE_LIMIT_ORDER, onDeleteLimitOrder);
  yield takeEvery(ACTION_CONSUME_LIMIT_ORDER, onConsumeLimitOrder);
  yield takeLatest(ACTION_UPDATE_LIMIT_ORDER_PRICE, onUpdateLimitOrder);
}
